import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { API_CONSTANTS } from '@app/core/constants/api.constants';

export interface ApiConfig {
    retryCount?: number;
    timeoutMs?: number;
}

/**
 * Base service for all HTTP operations
 * Provides unified error handling, retry logic, and request timeout
 */
@Injectable({
    providedIn: 'root',
})
export abstract class BaseApiService {
    protected readonly baseUrl: string;
    protected readonly defaultConfig: ApiConfig = {
        retryCount: API_CONSTANTS.RETRY.DEFAULT_COUNT,
        timeoutMs: API_CONSTANTS.TIMEOUTS.DEFAULT,
    };

    constructor(
        protected http: HttpClient,
        protected endpoint: string
    ) {
        this.baseUrl = `${environment.API_URL}${endpoint}`;
    }

    /**
     * Performs a GET request with credentials, optional query params, and timeout.
     * @param path - Path segment appended to the service base URL
     * @param params - Query parameters as HttpParams or a plain object
     * @param config - Optional retry count and timeout overrides
     * @param override - Extra HttpClient options (e.g. observe, responseType)
     * @returns Observable of the response body
     */
    protected get<T>(
        path: string = '',
        params?: HttpParams | Record<string, unknown>,
        config?: ApiConfig,
        override?: Record<string, unknown>
    ): Observable<T> {
        const url = this.buildUrl(path);
        const headers = this.buildHeaders(config);
        const httpParams = this.buildParams(params);
        const requestConfig = { ...this.defaultConfig, ...config };

        return this.http
            .get<T>(url, { headers, withCredentials: true, params: httpParams, ...(override ?? {}) })
            .pipe(timeout(requestConfig.timeoutMs!));
    }

    /**
     * Performs a POST request with credentials and timeout.
     * @param path - Path segment appended to the service base URL
     * @param body - Request payload
     * @param config - Optional retry count and timeout overrides
     * @returns Observable of the response body
     */
    protected post<T>(path: string = '', body: unknown, config?: ApiConfig): Observable<T> {
        const url = this.buildUrl(path);
        const headers = this.buildHeaders(config);
        const requestConfig = { ...this.defaultConfig, ...config };

        return this.http.post<T>(url, body, { headers, withCredentials: true }).pipe(timeout(requestConfig.timeoutMs!));
    }

    /**
     * Performs a PATCH request with credentials and timeout.
     * @param path - Path segment appended to the service base URL
     * @param body - Request payload
     * @param config - Optional retry count and timeout overrides
     * @returns Observable of the response body
     */
    protected patch<T>(path: string = '', body: unknown, config?: ApiConfig): Observable<T> {
        const url = this.buildUrl(path);
        const headers = this.buildHeaders(config);
        const requestConfig = { ...this.defaultConfig, ...config };

        return this.http.patch<T>(url, body, { headers, withCredentials: true }).pipe(timeout(requestConfig.timeoutMs!));
    }

    /**
     * Performs a PUT request with credentials and timeout.
     * @param path - Path segment appended to the service base URL
     * @param body - Request payload
     * @param config - Optional retry count and timeout overrides
     * @returns Observable of the response body
     */
    protected put<T>(path: string = '', body: unknown, config?: ApiConfig): Observable<T> {
        const url = this.buildUrl(path);
        const headers = this.buildHeaders(config);
        const requestConfig = { ...this.defaultConfig, ...config };

        return this.http.put<T>(url, body, { headers, withCredentials: true }).pipe(timeout(requestConfig.timeoutMs!));
    }

    /**
     * Performs a DELETE request with credentials and timeout.
     * @param path - Path segment appended to the service base URL
     * @param config - Optional retry count and timeout overrides
     * @returns Observable of the response body
     */
    protected delete<T>(path: string = '', config?: ApiConfig): Observable<T> {
        const url = this.buildUrl(path);
        const headers = this.buildHeaders(config);
        const requestConfig = { ...this.defaultConfig, ...config };

        return this.http.delete<T>(url, { headers, withCredentials: true }).pipe(timeout(requestConfig.timeoutMs!));
    }

    /**
     * Injects custom headers for retry control.
     */
    private buildHeaders(config?: ApiConfig): HttpHeaders {
        let headers = new HttpHeaders();
        const retryCount = config?.retryCount ?? (this.defaultConfig.retryCount || API_CONSTANTS.RETRY.DEFAULT_COUNT);

        headers = headers.set('X-Retry-Count', retryCount.toString());

        return headers;
    }

    /**
     * Builds a complete request URL
     */
    private buildUrl(path: string): string {
        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        const cleanBase = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
        return cleanPath ? `${cleanBase}/${cleanPath}` : cleanBase;
    }

    /**
     * Converts an object to HttpParams
     */
    private buildParams(params?: HttpParams | Record<string, unknown>): HttpParams | undefined {
        if (!params) return undefined;

        if (params instanceof HttpParams) return params;

        let httpParams = new HttpParams();
        Object.keys(params).forEach((key) => {
            const value = params[key];
            if (value !== null && value !== undefined) {
                httpParams = httpParams.set(key, String(value));
            }
        });

        return httpParams;
    }
}
